// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useCallback } from 'react';
import * as yup from 'yup';
import type { StepComponentProps } from '../../../types/wizard';
import { Dropdown, TextInput } from '@loa/ui-components';
import { toRawNumber, formatCurrencyDisplay, formatEuroBlur } from './currencyHelpers';

const PAYING_STATUS_OPTIONS = [
  { value: 'Paid Up', text: 'Paid Up' },
  { value: 'In Force', text: 'In Force' },
  { value: 'Deferred', text: 'Deferred' },
];

const PREMIUM_FREQUENCY_OPTIONS = [
  { value: 'Monthly', text: 'Monthly' },
  { value: 'Quarterly', text: 'Quarterly' },
  { value: 'Semi-Annual', text: 'Semi-Annual' },
  { value: 'Annual', text: 'Annual' },
  { value: 'Single', text: 'Single' },
  { value: 'N/A', text: 'N/A' },
];

const POLICY_PAID_BY_OPTIONS = [
  { value: 'Direct Debit', text: 'Direct Debit' },
  { value: 'Standing Order', text: 'Standing Order' },
  { value: 'Manual Adhoc Transfer', text: 'Manual Adhoc Transfer' },
];

const contributionSchema = yup.object({
  employerContribution: yup.string().trim().required('Employer contribution is required'),
  employeeContribution: yup.string().trim().required('Employee contribution is required'),
  payingStatus: yup.string().required('Paying status is required'),
  dateOfLastContribution: yup.string().required('Date of last contribution is required'),
  regularPremium: yup.string().trim().when('payingStatus', {
    is: 'In Force',
    then: (schema) => schema.required('Regular premium is required'),
    otherwise: (schema) => schema.optional(),
  }),
  regularPremiumFrequency: yup.string().when('payingStatus', {
    is: 'In Force',
    then: (schema) => schema.required('Regular premium frequency is required'),
    otherwise: (schema) => schema.optional(),
  }),
  singlePremium: yup.string().trim().when('payingStatus', {
    is: 'In Force',
    then: (schema) => schema.required('Single premium is required'),
    otherwise: (schema) => schema.optional(),
  }),
  singlePremiumFrequency: yup.string().when('payingStatus', {
    is: 'In Force',
    then: (schema) => schema.required('Single premium frequency is required'),
    otherwise: (schema) => schema.optional(),
  }),
  policyPaidBy: yup.string().when('payingStatus', {
    is: 'In Force',
    then: (schema) => schema.required('This field is required'),
    otherwise: (schema) => schema.optional(),
  }),
  dueDateNextPayment: yup.string().when('payingStatus', {
    is: 'In Force',
    then: (schema) => schema.required('Due date of next payment is required'),
    otherwise: (schema) => schema.optional(),
  }),
  dueAmountNextPayment: yup.string().trim().when('payingStatus', {
    is: 'In Force',
    then: (schema) => schema.required('Due amount is required'),
    otherwise: (schema) => schema.optional(),
  }),
  transfersInFromAnotherScheme: yup.string().trim().required('Transfers in amount is required'),
});

function validateField(fieldName: string, data: Record<string, unknown>): string | undefined {
  try {
    contributionSchema.validateSyncAt(fieldName, data);
    return undefined;
  } catch (err) {
    if (err instanceof yup.ValidationError) return err.message;
    return undefined;
  }
}

export function StepContributionDetails({
  data,
  onFieldChange,
  fieldErrors,
  onClearFieldError,
}: Readonly<StepComponentProps>) {
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [inlineErrors, setInlineErrors] = useState<Record<string, string>>({});

  const handleBlur = useCallback((field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const error = validateField(field, data);
    setInlineErrors((prev) => {
      if (error) return { ...prev, [field]: error };
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, [data]);

  const getError = (field: string): string | undefined => {
    return (touched[field] ? inlineErrors[field] : undefined) || fieldErrors[field];
  };

  const handleCurrencyChange = useCallback((field: string, inputValue: string) => {
    const raw = toRawNumber(inputValue);
    if (raw && !/^\d*\.?\d*$/.test(raw)) return;
    onFieldChange(field, raw);
    onClearFieldError(field);
  }, [onFieldChange, onClearFieldError]);

  const handleCurrencyBlur = useCallback((field: string) => {
    const raw = (data[field] as string) || '';
    if (raw) {
      onFieldChange(field, formatEuroBlur(raw));
    }
    handleBlur(field);
  }, [data, onFieldChange, handleBlur]);

  const getCurrencyDisplay = (field: string): string => {
    const raw = (data[field] as string) || '';
    return raw ? formatCurrencyDisplay(raw) : '';
  };

  // Date picker SVG icon (calendar)
  const calendarIcon = (maskId: string) => (
    <svg className="absolute right-[16px] top-1/2 -translate-y-1/2 pointer-events-none" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <mask id={maskId} style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="0" y="0" width="16" height="16">
        <path d="M16 0H0V16H16V0Z" fill="white"/>
      </mask>
      <g mask={`url(#${maskId})`}>
        <path d="M5.3335 3.83333C5.06016 3.83333 4.8335 3.60666 4.8335 3.33333V1.33333C4.8335 1.05999 5.06016 0.833328 5.3335 0.833328C5.60683 0.833328 5.8335 1.05999 5.8335 1.33333V3.33333C5.8335 3.60666 5.60683 3.83333 5.3335 3.83333Z" fill="#141414"/>
        <path d="M10.6665 3.83333C10.3932 3.83333 10.1665 3.60666 10.1665 3.33333V1.33333C10.1665 1.05999 10.3932 0.833328 10.6665 0.833328C10.9398 0.833328 11.1665 1.05999 11.1665 1.33333V3.33333C11.1665 3.60666 10.9398 3.83333 10.6665 3.83333Z" fill="#141414"/>
        <path d="M13.6668 6.55989H2.3335C2.06016 6.55989 1.8335 6.33322 1.8335 6.05989C1.8335 5.78656 2.06016 5.55989 2.3335 5.55989H13.6668C13.9402 5.55989 14.1668 5.78656 14.1668 6.05989C14.1668 6.33322 13.9402 6.55989 13.6668 6.55989Z" fill="#141414"/>
        <path d="M10.6667 15.1667H5.33333C2.9 15.1667 1.5 13.7667 1.5 11.3333V5.66666C1.5 3.23333 2.9 1.83333 5.33333 1.83333H10.6667C13.1 1.83333 14.5 3.23333 14.5 5.66666V11.3333C14.5 13.7667 13.1 15.1667 10.6667 15.1667ZM5.33333 2.83333C3.42667 2.83333 2.5 3.75999 2.5 5.66666V11.3333C2.5 13.24 3.42667 14.1667 5.33333 14.1667H10.6667C12.5733 14.1667 13.5 13.24 13.5 11.3333V5.66666C13.5 3.75999 12.5733 2.83333 10.6667 2.83333H5.33333Z" fill="#141414"/>
      </g>
    </svg>
  );

  const dateInputClass = (field: string) =>
    `w-full h-[48px] px-md pr-2xl rounded-lg border ${getError(field) ? 'border-[#E7000B]' : 'border-grey-04'} bg-white ${(data[field] as string) ? 'text-[#333]' : 'text-[#B6B6B6]'} font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-[48px] [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`;



  const currencyWrapper = (field: string) =>
    `flex items-center w-full h-[48px] px-md rounded-lg border ${getError(field) ? 'border-[#E7000B]' : 'border-grey-04'} bg-white focus-within:border-purple-primary`;

  return (
    <div className="flex flex-col gap-lg">
      <div className="text-black font-sans text-h5 font-normal leading-normal not-italic">
        Contribution Details
      </div>

      {/* Row 1: Employer Contribution to date* | Employee Contribution to date* */}
      <div className="flex flex-row gap-lg">
        <div className="flex flex-col gap-sm flex-1">
          <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
            Employer Contribution to date <span className="text-[#E7000B]">*</span>
          </div>
          <div className={currencyWrapper('employerContribution')}>
            <span className="text-black font-sans text-body-02 font-normal leading-normal select-none" style={{ marginRight: '8px' }}>€</span>
            <input
              type="text"
              inputMode="decimal"
              value={getCurrencyDisplay('employerContribution')}
              onChange={(e) => handleCurrencyChange('employerContribution', e.target.value)}
              onBlur={() => handleCurrencyBlur('employerContribution')}
              className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
            />
          </div>
          {getError('employerContribution') && (
            <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('employerContribution')}</div>
          )}
        </div>

        <div className="flex flex-col gap-sm flex-1">
          <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
            Employee Contribution to date <span className="text-[#E7000B]">*</span>
          </div>
          <div className={currencyWrapper('employeeContribution')}>
            <span className="text-black font-sans text-body-02 font-normal leading-normal select-none" style={{ marginRight: '8px' }}>€</span>
            <input
              type="text"
              inputMode="decimal"
              value={getCurrencyDisplay('employeeContribution')}
              onChange={(e) => handleCurrencyChange('employeeContribution', e.target.value)}
              onBlur={() => handleCurrencyBlur('employeeContribution')}
              className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
            />
          </div>
          {getError('employeeContribution') && (
            <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('employeeContribution')}</div>
          )}
        </div>
      </div>

      {/* Row 2: Paying status* | Date of last contribution* */}
      <div className="flex flex-row gap-lg">
        <div className="flex flex-col gap-sm flex-1">
          <Dropdown
            label="Paying status"
            placeholder="Choose an option"
            options={PAYING_STATUS_OPTIONS}
            value={(data.payingStatus as string) || ''}
            onChange={(value) => {
              onFieldChange('payingStatus', value);
              onClearFieldError('payingStatus');
              handleBlur('payingStatus');
            }}
            error={getError('payingStatus')}
            required
          />
        </div>

        <div className="flex flex-col gap-sm flex-1">
          <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
            Date of last contribution <span className="text-[#E7000B]">*</span>
          </div>
          <div className="relative">
            <input
              type="text"
              placeholder="DD/MM/YYYY"
              value={(data.dateOfLastContribution as string) || ''}
              onFocus={(e) => { e.currentTarget.type = 'date'; }}
              onBlur={(e) => {
                if (!e.currentTarget.value) e.currentTarget.type = 'text';
                handleBlur('dateOfLastContribution');
              }}
              onChange={(e) => {
                onFieldChange('dateOfLastContribution', e.target.value);
                onClearFieldError('dateOfLastContribution');
              }}
              className={dateInputClass('dateOfLastContribution')}
            />
            {calendarIcon('mask_dlc')}
          </div>
          {getError('dateOfLastContribution') && (
            <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('dateOfLastContribution')}</div>
          )}
        </div>
      </div>

      {/* Rows 3-6: Only shown when paying status is "In Force" */}
      {(data.payingStatus as string) === 'In Force' && (
        <>
          {/* Row 3: Regular Premium* | Regular Premium frequency* */}
          <div className="flex flex-row gap-lg">
            <div className="flex flex-col gap-sm flex-1">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Regular Premium <span className="text-[#E7000B]">*</span>
              </div>
              <div className={currencyWrapper('regularPremium')}>
                <span className="text-black font-sans text-body-02 font-normal leading-normal select-none" style={{ marginRight: '8px' }}>€</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={getCurrencyDisplay('regularPremium')}
                  onChange={(e) => handleCurrencyChange('regularPremium', e.target.value)}
                  onBlur={() => handleCurrencyBlur('regularPremium')}
                  className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
                />
              </div>
              {getError('regularPremium') && (
                <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('regularPremium')}</div>
              )}
            </div>

            <div className="flex flex-col gap-sm flex-1">
              <Dropdown
                label="Regular Premium frequency"
                placeholder="Choose an option"
                options={PREMIUM_FREQUENCY_OPTIONS}
                value={(data.regularPremiumFrequency as string) || ''}
                onChange={(value) => {
                  onFieldChange('regularPremiumFrequency', value);
                  onClearFieldError('regularPremiumFrequency');
                  handleBlur('regularPremiumFrequency');
                }}
                error={getError('regularPremiumFrequency')}
                required
              />
            </div>
          </div>

          {/* Row 4: Single Premium* | Single Premium frequency* */}
          <div className="flex flex-row gap-lg">
            <div className="flex flex-col gap-sm flex-1">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Single Premium <span className="text-[#E7000B]">*</span>
              </div>
              <div className={currencyWrapper('singlePremium')}>
                <span className="text-black font-sans text-body-02 font-normal leading-normal select-none" style={{ marginRight: '8px' }}>€</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={getCurrencyDisplay('singlePremium')}
                  onChange={(e) => handleCurrencyChange('singlePremium', e.target.value)}
                  onBlur={() => handleCurrencyBlur('singlePremium')}
                  className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
                />
              </div>
              {getError('singlePremium') && (
                <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('singlePremium')}</div>
              )}
            </div>

            <div className="flex flex-col gap-sm flex-1">
              <Dropdown
                label="Single Premium frequency"
                placeholder="Choose an option"
                options={PREMIUM_FREQUENCY_OPTIONS}
                value={(data.singlePremiumFrequency as string) || ''}
                onChange={(value) => {
                  onFieldChange('singlePremiumFrequency', value);
                  onClearFieldError('singlePremiumFrequency');
                  handleBlur('singlePremiumFrequency');
                }}
                error={getError('singlePremiumFrequency')}
                required
              />
            </div>
          </div>

          {/* Row 5: Indexation details if applicable | Is the policy paid by* */}
          <div className="flex flex-row gap-lg">
            <div className="flex flex-col gap-sm flex-1">
              <TextInput
                label="Indexation details if applicable"
                value={(data.indexationDetails as string) || ''}
                onChange={(e) => {
                  onFieldChange('indexationDetails', e.target.value);
                }}
                fluid
              />
            </div>

            <div className="flex flex-col gap-sm flex-1">
              <Dropdown
                label="Is the policy paid by"
                placeholder="Choose an option"
                options={POLICY_PAID_BY_OPTIONS}
                value={(data.policyPaidBy as string) || ''}
                onChange={(value) => {
                  onFieldChange('policyPaidBy', value);
                  onClearFieldError('policyPaidBy');
                  handleBlur('policyPaidBy');
                }}
                error={getError('policyPaidBy')}
                required
              />
            </div>
          </div>

          {/* Row 6: Due date of next payment* | Due amount of next payment* */}
          <div className="flex flex-row gap-lg">
            <div className="flex flex-col gap-sm flex-1">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Due date of next payment <span className="text-[#E7000B]">*</span>
              </div>
              <div className="relative">
                <input
                  type="text"
                  placeholder="DD/MM/YYYY"
                  value={(data.dueDateNextPayment as string) || ''}
                  onFocus={(e) => { e.currentTarget.type = 'date'; }}
                  onBlur={(e) => {
                    if (!e.currentTarget.value) e.currentTarget.type = 'text';
                    handleBlur('dueDateNextPayment');
                  }}
                  onChange={(e) => {
                    onFieldChange('dueDateNextPayment', e.target.value);
                    onClearFieldError('dueDateNextPayment');
                  }}
                  className={dateInputClass('dueDateNextPayment')}
                />
                {calendarIcon('mask_ddnp')}
              </div>
              {getError('dueDateNextPayment') && (
                <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('dueDateNextPayment')}</div>
              )}
            </div>

            <div className="flex flex-col gap-sm flex-1">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Due amount of next payment <span className="text-[#E7000B]">*</span>
              </div>
              <div className={currencyWrapper('dueAmountNextPayment')}>
                <span className="text-black font-sans text-body-02 font-normal leading-normal select-none" style={{ marginRight: '8px' }}>€</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={getCurrencyDisplay('dueAmountNextPayment')}
                  onChange={(e) => handleCurrencyChange('dueAmountNextPayment', e.target.value)}
                  onBlur={() => handleCurrencyBlur('dueAmountNextPayment')}
                  className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
                />
              </div>
              {getError('dueAmountNextPayment') && (
                <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('dueAmountNextPayment')}</div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Row 7: Transfers in from another scheme* (single field) */}
      <div className="flex flex-row gap-lg">
        <div className="flex flex-col gap-sm flex-1">
          <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
            Transfers in from another scheme <span className="text-[#E7000B]">*</span>
          </div>
          <div className={currencyWrapper('transfersInFromAnotherScheme')}>
            <span className="text-black font-sans text-body-02 font-normal leading-normal select-none" style={{ marginRight: '8px' }}>€</span>
            <input
              type="text"
              inputMode="decimal"
              value={getCurrencyDisplay('transfersInFromAnotherScheme')}
              onChange={(e) => handleCurrencyChange('transfersInFromAnotherScheme', e.target.value)}
              onBlur={() => handleCurrencyBlur('transfersInFromAnotherScheme')}
              className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
            />
          </div>
          {getError('transfersInFromAnotherScheme') && (
            <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('transfersInFromAnotherScheme')}</div>
          )}
        </div>
        <div className="flex-1" />
      </div>
    </div>
  );
}
